import {
  isAudioClip,
  isMidiClip,
  type AudioClip,
  type Clip,
  type MidiClip,
  type Project,
  type Track,
} from './types';
import { normaliseSignatureMap, normaliseTempoMap, secondsSpanToTicks, secondsToTicks, ticksToSeconds } from '@/timing/tempoMap';
import { beatAtSource, beatToSourceSec, warpCurve, warpTotalBeats } from './warpMap';
import { MAX_PLAYBACK_RATE, MIN_PLAYBACK_RATE } from './project';

/**
 * Pure, memo-free derivations over a Project. Everything here is cheap enough
 * to call per render for the visible slice; anything expensive (waveforms,
 * decoded audio) belongs to the asset layer, not to selectors.
 */

export function tempoMapOf(project: Project) {
  return normaliseTempoMap(project.transport.tempoMap, project.transport.ppq);
}

export function signatureMapOf(project: Project) {
  return normaliseSignatureMap(project.transport.timeSignatures, project.transport.ppq);
}

/**
 * How many quarter notes a clip that follows the tempo occupies.
 *
 * This is the whole idea in one line: a recording at a known tempo is a fixed
 * amount of *music*, and music is measured in beats. Its length in ticks
 * therefore does not depend on the project tempo at all — only its length in
 * seconds does. Every conversion for a following clip goes through beats, which
 * is what lets one bend correctly through a tempo change rather than picking a
 * single rate at its start and being wrong for the rest of its length.
 */
export function warpBeats(clip: AudioClip): number | null {
  return warpTotalBeats(clip);
}

/** Length of a clip on the timeline, in ticks. */
export function clipLengthTicks(project: Project, clip: Clip): number {
  if (isMidiClip(clip)) return clip.durationTicks;
  const beats = warpBeats(clip);
  if (beats !== null) return Math.max(1, Math.round(beats * project.transport.ppq));
  const map = tempoMapOf(project);
  const effectiveSec = clip.durationSec / Math.max(0.01, clip.playbackRate);
  return Math.max(1, secondsSpanToTicks(map, clip.startTicks, effectiveSec));
}

/** How long a clip lasts on the timeline, in wall-clock seconds. */
export function clipTimelineSeconds(project: Project, clip: AudioClip): number {
  const map = tempoMapOf(project);
  return ticksToSeconds(map, clipEndTicks(project, clip)) - ticksToSeconds(map, clip.startTicks);
}

/**
 * Source seconds consumed by a span of the timeline inside a clip.
 *
 * For a fixed clip this is the timeline span times its rate. For a following
 * clip it is the span's *beats* at the clip's own recorded tempo, which is exact
 * even where the project tempo changes part-way through the span.
 */
export function sourceSecondsBetween(
  project: Project,
  clip: AudioClip,
  fromTick: number,
  toTick: number,
): number {
  if (clip.warpSourceBpm !== null) {
    // Through the warp curve, so a clip whose markers pull its middle around
    // still consumes exactly the source that lies under the span being asked
    // about — trimming one has to cut where the audio actually is.
    const curve = warpCurve(clip);
    const fromBeat = (fromTick - clip.startTicks) / project.transport.ppq;
    const toBeat = (toTick - clip.startTicks) / project.transport.ppq;
    return beatToSourceSec(curve, toBeat) - beatToSourceSec(curve, fromBeat);
  }
  const map = tempoMapOf(project);
  return (ticksToSeconds(map, toTick) - ticksToSeconds(map, fromTick)) * Math.max(0.01, clip.playbackRate);
}

/**
 * The inverse: the tick at which a clip will have consumed `sourceSec` more of
 * its source than it had at `fromTick`. Negative to reach back before it.
 *
 * Outside the clip's own span there is no curve to follow, so a following clip
 * is extended at its recorded tempo; that is what "the same clip, a little
 * longer" has to mean, and what the even mapping would give anyway.
 */
export function tickAfterSourceSeconds(
  project: Project,
  clip: AudioClip,
  fromTick: number,
  sourceSec: number,
): number {
  const ppq = project.transport.ppq;
  if (clip.warpSourceBpm !== null) {
    const fromBeat = (fromTick - clip.startTicks) / ppq;
    const target = beatToSourceSec(warpCurve(clip), fromBeat) + sourceSec;
    return clip.startTicks + beatAtSource(clip, target) * ppq;
  }
  const map = tempoMapOf(project);
  return secondsToTicks(map, ticksToSeconds(map, fromTick) + sourceSec / Math.max(0.01, clip.playbackRate));
}

/**
 * The average speed a clip plays at: its source length over its timeline length.
 *
 * For a fixed clip this is exactly `playbackRate`. For a following clip it is
 * what the tempo works out to — a single number, so it is what the inspector
 * shows and what the constant-ratio render path uses; a clip that bends through
 * a tempo change is rendered span by span instead, and does not go through here.
 */
export function effectivePlaybackRate(project: Project, clip: AudioClip): number {
  if (clip.warpSourceBpm === null) return Math.max(0.01, clip.playbackRate);
  const timelineSec = clipTimelineSeconds(project, clip);
  if (timelineSec <= 0) return MAX_PLAYBACK_RATE;
  return Math.min(MAX_PLAYBACK_RATE, Math.max(MIN_PLAYBACK_RATE, clip.durationSec / timelineSec));
}

export function clipEndTicks(project: Project, clip: Clip): number {
  return clip.startTicks + clipLengthTicks(project, clip);
}

/** Absolute start of a clip in seconds from project zero. */
export function clipStartSeconds(project: Project, clip: Clip): number {
  return ticksToSeconds(tempoMapOf(project), clip.startTicks);
}

export function tracksInOrder(project: Project): Track[] {
  return project.trackOrder.map((id) => project.tracks[id]).filter((t): t is Track => Boolean(t));
}

export function masterTrack(project: Project): Track {
  return project.tracks[project.masterTrackId];
}

export function clipsOfTrack(project: Project, trackId: string): Clip[] {
  const track = project.tracks[trackId];
  if (!track) return [];
  return track.clipIds.map((id) => project.clips[id]).filter((c): c is Clip => Boolean(c));
}

export function audioClipsOfTrack(project: Project, trackId: string): AudioClip[] {
  return clipsOfTrack(project, trackId).filter(isAudioClip);
}

export function midiClipsOfTrack(project: Project, trackId: string): MidiClip[] {
  return clipsOfTrack(project, trackId).filter(isMidiClip);
}

/** True when at least one non-master track is soloed. */
export function anySolo(project: Project): boolean {
  return project.trackOrder.some((id) => project.tracks[id]?.solo);
}

/**
 * Whether a track should be audible, accounting for solo state and for mutes
 * anywhere along its routing path to the master bus.
 */
export function isTrackAudible(project: Project, trackId: string): boolean {
  const soloing = anySolo(project);
  const seen = new Set<string>();
  let current: string | null = trackId;
  let first = true;
  while (current && !seen.has(current)) {
    seen.add(current);
    const track: Track | undefined = project.tracks[current];
    if (!track) return false;
    if (track.mute) return false;
    if (first && soloing && track.kind !== 'master' && track.kind !== 'aux' && track.kind !== 'bus' && !track.solo) {
      return false;
    }
    first = false;
    current = track.outputTrackId;
  }
  return true;
}

/** Furthest point reached by any clip, in ticks. Used for export length and scrolling. */
export function projectEndTicks(project: Project): number {
  let end = 0;
  for (const clip of Object.values(project.clips)) {
    const e = clipEndTicks(project, clip);
    if (e > end) end = e;
  }
  return end;
}

export function projectDurationSeconds(project: Project): number {
  return ticksToSeconds(tempoMapOf(project), projectEndTicks(project));
}

/**
 * Routing order for graph construction: a track always appears after every
 * track that feeds it, so buses can be built before their sources connect.
 * Cycles are broken defensively rather than throwing, because a corrupt or
 * hand-edited project should still open.
 */
export function routingOrder(project: Project): string[] {
  const visited = new Set<string>();
  const out: string[] = [];
  const visit = (id: string, stack: Set<string>): void => {
    if (visited.has(id) || stack.has(id)) return;
    stack.add(id);
    const track = project.tracks[id];
    if (track?.outputTrackId) visit(track.outputTrackId, stack);
    stack.delete(id);
    if (!visited.has(id)) {
      visited.add(id);
      out.push(id);
    }
  };
  for (const id of [...project.trackOrder, project.masterTrackId]) visit(id, new Set());
  return out.reverse();
}

/** Detects whether setting `trackId`'s output to `targetId` would create a loop. */
export function wouldCreateRoutingCycle(project: Project, trackId: string, targetId: string | null): boolean {
  let current = targetId;
  const seen = new Set<string>([trackId]);
  while (current) {
    if (seen.has(current)) return true;
    seen.add(current);
    current = project.tracks[current]?.outputTrackId ?? null;
  }
  return false;
}
